import apiClient from '@shared/services/apiClient';
import type {
  ClinicClientAccount,
  CreateClinicClientAccountRequest,
  UpdateClinicClientAccountRequest,
  ClinicLocationClientAccount,
  CreateClinicLocationClientAccountRequest,
  CreateClinicLocationClientAccountsBatchRequest,
  UpdateClinicLocationClientAccountRequest,
  RelationshipNote,
  CreateRelationshipNoteRequest,
} from '../types/clientClinic';

// Org-level accounts
export const getClinicClientAccounts = async (status?: string): Promise<ClinicClientAccount[]> => {
  const params = status ? { status } : {};
  const response = await apiClient.get('/clinic-client-accounts', { params });
  return response.data;
};

export const getClinicClientAccountsByClinic = async (clinicId: number): Promise<ClinicClientAccount[]> => {
  const response = await apiClient.get(`/clinic-client-accounts/clinic/${clinicId}`);
  return response.data;
};

export const getClinicClientAccountsByCustomer = async (customerId: number): Promise<ClinicClientAccount[]> => {
  const response = await apiClient.get(`/clinic-client-accounts/customer/${customerId}`);
  return response.data;
};

export const createClinicClientAccount = async (
  request: CreateClinicClientAccountRequest
): Promise<ClinicClientAccount> => {
  const response = await apiClient.post('/clinic-client-accounts', request);
  return response.data;
};

export const updateClinicClientAccount = async (
  id: number,
  request: UpdateClinicClientAccountRequest
): Promise<ClinicClientAccount> => {
  const response = await apiClient.put(`/clinic-client-accounts/${id}`, request);
  return response.data;
};

export const deleteClinicClientAccount = async (id: number): Promise<void> => {
  await apiClient.delete(`/clinic-client-accounts/${id}`);
};

// Location-level accounts
export const getClinicLocationClientAccounts = async (
  status?: string,
  serviceType?: string
): Promise<ClinicLocationClientAccount[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (serviceType) params.serviceType = serviceType;
  const response = await apiClient.get('/clinic-location-client-accounts', { params });
  return response.data;
};

// Export the supplied rows (already filtered/sorted client-side, no pagination)
// as an Excel file generated by the backend.
export const exportClinicLocationClientAccounts = async (
  rows: ClinicLocationClientAccount[]
): Promise<Blob> => {
  const response = await apiClient.post('/clinic-location-client-accounts/export', rows, {
    responseType: 'blob',
    timeout: 120000,
  });
  return response.data;
};

export const getClinicLocationClientAccountsByClinic = async (
  clinicId: number,
  serviceType?: string
): Promise<ClinicLocationClientAccount[]> => {
  const params: Record<string, string> = {};
  if (serviceType) params.serviceType = serviceType;
  const response = await apiClient.get(`/clinic-location-client-accounts/clinic/${clinicId}`, { params });
  return response.data;
};

export const getClinicLocationClientAccountsByCustomer = async (
  customerId: number,
  serviceType?: string
): Promise<ClinicLocationClientAccount[]> => {
  const params: Record<string, string> = {};
  if (serviceType) params.serviceType = serviceType;
  const response = await apiClient.get(`/clinic-location-client-accounts/customer/${customerId}`, { params });
  return response.data;
};

export const getClinicLocationClientAccountsByLocation = async (
  customerLocationId: number
): Promise<ClinicLocationClientAccount[]> => {
  const response = await apiClient.get(`/clinic-location-client-accounts/location/${customerLocationId}`);
  return response.data;
};

export const getClinicLocationClientAccountById = async (
  id: number
): Promise<ClinicLocationClientAccount> => {
  const response = await apiClient.get(`/clinic-location-client-accounts/${id}`);
  return response.data;
};

export const setPrimaryLocationClinic = async (
  id: number
): Promise<ClinicLocationClientAccount> => {
  const response = await apiClient.put(`/clinic-location-client-accounts/${id}/set-primary`);
  return response.data;
};

export const createClinicLocationClientAccount = async (
  request: CreateClinicLocationClientAccountRequest
): Promise<ClinicLocationClientAccount> => {
  const response = await apiClient.post('/clinic-location-client-accounts', request);
  return response.data;
};

// SLV2-2919: dual-create — Clinical and/or InjuryMgmt rows for a (location,
// clinic) pair in one call; the server creates both in one DB transaction.
export const createClinicLocationClientAccounts = async (
  request: CreateClinicLocationClientAccountsBatchRequest
): Promise<ClinicLocationClientAccount[]> => {
  const response = await apiClient.post('/clinic-location-client-accounts/batch', request);
  return response.data;
};

export const updateClinicLocationClientAccount = async (
  id: number,
  request: UpdateClinicLocationClientAccountRequest
): Promise<ClinicLocationClientAccount> => {
  const response = await apiClient.put(`/clinic-location-client-accounts/${id}`, request);
  return response.data;
};

export const deleteClinicLocationClientAccount = async (id: number): Promise<void> => {
  await apiClient.delete(`/clinic-location-client-accounts/${id}`);
};

// Relationship notes
export const getOrgAccountNotes = async (accountId: number): Promise<RelationshipNote[]> => {
  const response = await apiClient.get(`/clinic-client-accounts/${accountId}/notes`);
  return response.data;
};

export const createOrgAccountNote = async (
  accountId: number,
  request: CreateRelationshipNoteRequest
): Promise<RelationshipNote> => {
  const response = await apiClient.post(`/clinic-client-accounts/${accountId}/notes`, request);
  return response.data;
};

export const getLocationAccountNotes = async (accountId: number): Promise<RelationshipNote[]> => {
  const response = await apiClient.get(`/clinic-location-client-accounts/${accountId}/notes`);
  return response.data;
};

export const createLocationAccountNote = async (
  accountId: number,
  request: CreateRelationshipNoteRequest
): Promise<RelationshipNote> => {
  const response = await apiClient.post(`/clinic-location-client-accounts/${accountId}/notes`, request);
  return response.data;
};

export const deleteRelationshipNote = async (noteId: number): Promise<void> => {
  await apiClient.delete(`/relationship-notes/${noteId}`);
};
